import React, { useState, useEffect } from 'react';
import { signUp, signIn, resetPassword, confirmResetPassword, resendSignUpCode } from '@aws-amplify/auth';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './CustomSignIn.css';
import ReactGA from 'react-ga4';
import { FaEye, FaEyeSlash, FaLock, FaEnvelope, FaUser, FaShieldAlt, FaChrome } from 'react-icons/fa';
import Header from './components/Header';
import Footer from './components/Footer';

const CustomSignIn = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newPasswordsMatch, setNewPasswordsMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const recaptchaKey = '6LfgcCEqAAAAAImE1F2rQD8vG_ADWXQ0senGNdND';
  const from = location.state?.from?.pathname || '/app';

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  useEffect(() => {
    // Check if reCAPTCHA script is loaded
    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        setRecaptchaLoaded(true);
        console.log('reCAPTCHA script loaded and ready');
      } else {
        console.log('reCAPTCHA script not ready yet, retrying...');
        setTimeout(checkRecaptcha, 1000);
      }
    };
    
    checkRecaptcha();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    if (!captchaToken) {
      setError('Please complete the CAPTCHA');
      setLoading(false);
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        await signUp({
          username: email,
          password: password,
          attributes: { email },
        });

        setError('Sign up successful. Please check your email for verification.');
        setIsSignUp(false);
      } else {
        await signIn({
          username: email,
          password: password,
        });
        setError('');
        navigate(from);
      }
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        setError('Account created but not confirmed. Please check your email for verification.');
        try {
          await resendSignUpCode(email);
          setError('Verification code resent. Please check your email.');
        } catch (resendError) {
          setError(`Error resending verification code: ${resendError.message}`);
        }
      } else {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resetPassword({ username: email });
      setError('A reset code has been sent to your email.');
      setIsResetPassword(true);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!email || !resetCode || !newPassword || !confirmNewPassword) {
      setError('Please fill in all fields to reset your password.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      setNewPasswordsMatch(false);
      return;
    }

    setLoading(true);
    setError('');
    setNewPasswordsMatch(true);

    try {
      await confirmResetPassword({ username: email, confirmationCode: resetCode, newPassword });
      setError('Password has been reset successfully. You can now log in.');
      setIsResetPassword(false);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const toggleSignUp = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setCaptchaToken(null);
    setPasswordsMatch(true);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (isSignUp) {
      setPasswordsMatch(e.target.value === confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setPasswordsMatch(password === e.target.value);
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setNewPasswordsMatch(e.target.value === confirmNewPassword);
  };

  const handleConfirmNewPasswordChange = (e) => {
    setConfirmNewPassword(e.target.value);
    setNewPasswordsMatch(newPassword === e.target.value);
  };

  const getPageTitle = () => {
    if (isResetPassword) return 'Reset Password';
    if (isSignUp) return 'Create Account';
    return 'Welcome Back';
  };

  const getPageSubtitle = () => {
    if (isResetPassword) return 'Enter your reset code and new password';
    if (isSignUp) return 'Join thousands of users organizing their ideas';
    return 'Sign in to continue to your account';
  };

  useEffect(() => {
    // Add class to body/html to allow scrolling on auth page
    document.body.classList.add('auth-page-active');
    document.documentElement.classList.add('auth-page-active');
    
    return () => {
      // Clean up on unmount
      document.body.classList.remove('auth-page-active');
      document.documentElement.classList.remove('auth-page-active');
    };
  }, []);

  return (
    <div className="auth-page">
      <Header currentPage="auth" />
      
      {/* Main Content */}
      <div className="auth-main">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header-section">
              <h1 className="auth-title">{getPageTitle()}</h1>
              <p className="auth-subtitle">{getPageSubtitle()}</p>
            </div>

            <div className="auth-browser-hint">
              <FaChrome className="auth-browser-hint-icon" />
              <span>Chrome is recommended for the best experience.</span>
            </div>

            {error && (
              <div className={`auth-message ${error.includes('successful') || error.includes('sent') ? 'success' : 'error'}`}>
                {error}
              </div>
            )}

            {isResetPassword ? (
              <form onSubmit={(e) => e.preventDefault()} className="auth-form">
                <div className="form-group">
                  <label htmlFor="resetEmail" className="form-label">
                    <FaEnvelope className="form-icon" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="resetEmail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="resetCode" className="form-label">
                    <FaShieldAlt className="form-icon" />
                    Reset Code
                  </label>
                  <input
                    type="text"
                    id="resetCode"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="form-input"
                    placeholder="Enter reset code from email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">
                    <FaLock className="form-icon" />
                    New Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      className="form-input"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmNewPassword" className="form-label">
                    <FaLock className="form-icon" />
                    Confirm New Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmNewPassword ? "text" : "password"}
                      id="confirmNewPassword"
                      value={confirmNewPassword}
                      onChange={handleConfirmNewPasswordChange}
                      className="form-input"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    >
                      {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {!newPasswordsMatch && <p className="form-error">Passwords do not match</p>}
                </div>

                <button
                  type="button"
                  className="auth-button primary"
                  onClick={handleResetPassword}
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>

                <div className="auth-links">
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => setIsResetPassword(false)}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <FaEnvelope className="form-icon" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    <FaLock className="form-icon" />
                    Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={handlePasswordChange}
                      className="form-input"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {isSignUp && (
                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      <FaLock className="form-icon" />
                      Confirm Password
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        className="form-input"
                        placeholder="Confirm your password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {!passwordsMatch && <p className="form-error">Passwords do not match</p>}
                  </div>
                )}

                <div className="captcha-container">
                  {recaptchaLoaded ? (
                    <ReCAPTCHA
                      sitekey={recaptchaKey}
                      onChange={handleCaptchaChange}
                      onErrored={() => console.error('reCAPTCHA error occurred')}
                      onExpired={() => console.log('reCAPTCHA expired')}
                      onLoad={() => console.log('reCAPTCHA loaded successfully')}
                      className="captcha"
                      theme="light"
                      size="normal"
                    />
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      Loading reCAPTCHA...
                    </div>
                  )}
                </div>

                <button type="submit" className="auth-button primary" disabled={loading}>
                  {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
                </button>

                {!isSignUp && (
                  <div className="auth-links">
                    <button
                      type="button"
                      className="auth-link"
                      onClick={handleForgotPassword}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </form>
            )}

            <div className="auth-footer">
              <div className="auth-toggle">
                <span className="auth-toggle-text">
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                </span>
                <button
                  type="button"
                  className="auth-toggle-link"
                  onClick={toggleSignUp}
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomSignIn;
